import React from "react";
import { Box, Button, Chip, Slider, Stack, TextField, Typography } from "@mui/material";
import { IndividualTextColorControl } from "../inspector/LandingInspectorControls";
import { elementTextFallback, normalizeEditableColor } from "../inspector/inspectorModel";
import {
  buildElementColorVisualStyle,
  buildElementFontScaleVisualStyle,
  currentElementColor,
  currentElementFontScale,
  effectiveSectionPalette,
  getValueAtDescriptorPath,
  sectionVisualPath,
} from "../elementEditing";
import {
  HERO_TEXT_FONT_SCALE_MAX,
  HERO_TEXT_FONT_SCALE_MIN,
  HERO_TEXT_FONT_SCALE_STEP,
  isHeroTextFontScalePath,
} from "../heroTextStyle";

function heroInheritedTextColor(form, descriptor, path, palette) {
  if (String(descriptor?.sectionType || "") !== "hero") return "";
  const visual = descriptor?.layout?.segment?.visualStyle || {};
  if (visual.useGlobalColors === false) {
    return palette.onSecondary || "#FFFFFF";
  }
  const candidate = String(path || "").toLowerCase();
  if (candidate.includes("eyebrow")) return "rgba(255, 255, 255, 0.78)";
  if (candidate.includes("subtitle")) return "rgba(255, 255, 255, 0.88)";
  if (candidate.includes("title")) {
    const hasPhrases = Array.isArray(form?.hero?.rotatingPhrases) &&
      form.hero.rotatingPhrases.some((value) => Boolean(String(value || "").trim()));
    return hasPhrases ? (form?.theme?.primaryColor || "#E44E31") : "#FFFFFF";
  }
  return palette.onSecondary || "#FFFFFF";
}

function appearanceTextFallback(form, descriptor, palette) {
  const sectionType = descriptor?.sectionType || descriptor?.sectionId || "";
  const appearance = form?.appearance?.[sectionType] || {};
  const path = String(descriptor?.fieldPath || descriptor?.canvasPath || "");
  const heroFallback = heroInheritedTextColor(form, descriptor, path, palette);
  if (heroFallback) return heroFallback;
  const normalizedPath = path.toLowerCase();
  if (normalizedPath.includes("eyebrow")) return appearance.accentColor || palette.primary;
  if (normalizedPath.includes("title") || normalizedPath.endsWith(".name")) return appearance.titleColor || palette.secondary || palette.text;
  if (normalizedPath.includes("subtitle") || normalizedPath.includes("description")) return appearance.textColor || palette.mutedText || palette.text;
  return elementTextFallback(path, palette, sectionType);
}

export default function TextElementEditor({ descriptor, form, disabled, onCommit }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  const current = String(getValueAtDescriptorPath(form, path, "") ?? "");
  const [draft, setDraft] = React.useState(current);

  React.useEffect(() => setDraft(current), [current, path]);
  if (!field || !path) return null;

  const palette = effectiveSectionPalette(form, descriptor);
  const fallback = normalizeEditableColor(appearanceTextFallback(form, descriptor, palette)) || "#000000";
  const colorPath = descriptor?.canvasPath || path;
  const ownColor = currentElementColor(form, descriptor, colorPath);
  const ownFontScale = currentElementFontScale(form, descriptor, colorPath);
  const sizeEligible = String(descriptor?.sectionType || "") === "hero" && isHeroTextFontScalePath(colorPath);
  const sliderScale = ownFontScale ?? 1;
  const visualPath = sectionVisualPath(descriptor);

  const commitText = () => {
    if (draft !== current) onCommit?.(path, draft);
  };
  const commitColor = (nextColor) => {
    if (!visualPath) return;
    onCommit?.(visualPath, buildElementColorVisualStyle(form, descriptor, colorPath, nextColor));
  };
  const commitFontScale = (nextScale) => {
    if (!visualPath) return;
    onCommit?.(visualPath, buildElementFontScaleVisualStyle(form, descriptor, colorPath, nextScale));
  };

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
        <Chip size="small" label={ownColor ? "Color propio" : "Color heredado"} variant={ownColor ? "filled" : "outlined"} />
        {sizeEligible ? (
          <Chip
            size="small"
            label={ownFontScale === null ? "Tamaño heredado" : `Tamaño ${Math.round(ownFontScale * 100)}%`}
            variant={ownFontScale === null ? "outlined" : "filled"}
          />
        ) : null}
      </Stack>
      <TextField
        fullWidth
        size="small"
        label={field.label || descriptor.label || "Texto"}
        value={draft}
        disabled={disabled}
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 4 : undefined}
        inputProps={{ maxLength: field.maxLength }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitText}
        onKeyDown={(event) => {
          if (!field.type?.includes?.("textarea") && event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <IndividualTextColorControl
        fieldPath={colorPath}
        label={field.label || descriptor.label || "Texto"}
        value={ownColor}
        fallback={fallback}
        disabled={disabled}
        onChange={commitColor}
        onReset={() => commitColor("")}
      />
      {sizeEligible ? (
        <Box sx={{ p: 1, borderRadius: 2.25, bgcolor: "rgba(25,36,47,.035)", border: "1px solid rgba(25,36,47,.08)" }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.64)", fontWeight: 900 }}>
              Tamaño · {ownFontScale === null ? "Predeterminado (100%)" : `${Math.round(sliderScale * 100)}%`}
            </Typography>
            <Button
              size="small"
              disabled={disabled || ownFontScale === null}
              onClick={() => commitFontScale(1)}
              sx={{ minWidth: 0, px: 0.9, textTransform: "none", fontWeight: 850 }}
            >
              Heredar
            </Button>
          </Stack>
          <Slider
            size="small"
            min={HERO_TEXT_FONT_SCALE_MIN}
            max={HERO_TEXT_FONT_SCALE_MAX}
            step={HERO_TEXT_FONT_SCALE_STEP}
            value={sliderScale}
            disabled={disabled}
            marks={[
              { value: HERO_TEXT_FONT_SCALE_MIN, label: "70%" },
              { value: 1, label: "100%" },
              { value: HERO_TEXT_FONT_SCALE_MAX, label: "140%" },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(Number(value) * 100)}%`}
            aria-label={`Tamaño de ${field.label || descriptor.label || "texto"}`}
            onChange={(_, value) => commitFontScale(Array.isArray(value) ? value[0] : value)}
            sx={{ px: 0.25 }}
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.4, color: "rgba(25,36,47,.58)" }}>
            Escala el tamaño responsive de este texto sin fijarlo en píxeles.
          </Typography>
        </Box>
      ) : null}
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>
        Heredar elimina únicamente el ajuste propio y conserva los demás estilos del texto.
      </Typography>
    </Stack>
  );
}
